import { useEffect, useMemo, useState } from 'react'
import dayjs from '@dcp/shared/utils/dayjs'
import { useFieldArray, useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from '@dcp/shared'
import { Copy, Trash2, Plus, Loader2, Info } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import {
  useUpdateManualTransaction,
  useCreateManualTransactions,
  useFacilities,
  useBankAccounts,
  useBankAccountsByIds,
} from '@/hooks/useBanking'
import { useCategories } from '@/hooks/useProjections'
import {
  Modal,
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DatePicker,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@atrvd/saas-ui'
import { useAuth } from '@/contexts/AuthContext'
import { maskAccountNumber } from '@/lib/utils'
import type { TenantTransaction } from '@/lib/db-tenant'
import { AmountInput } from './AmountInput'

// ---------------------------------------------------------------------------
// Batch creation form
// ---------------------------------------------------------------------------

/**
 * Zod schema for a single transaction row in the batch form.
 *
 * Every field is required. Two of them are UI-only and never persisted: `bank`
 * (not in this schema — it lives in component state) and `facility_id`. The
 * facility is required because it is what narrows the Bank / Account selects
 * (DCP-125), but DCP-324 made the account the sole carrier of a transaction's
 * facility, so the value is dropped at submit rather than written — see
 * `ManualTransactionInput`.
 */
const transactionRowSchema = z.object({
  facility_id: z.string().min(1, 'Facility is required'),
  bank_account_id: z.string().min(1, 'Account is required'),
  category_id: z.string().min(1, 'Category is required'),
  transaction_date: z.string().min(1, 'Date is required'),
  amount: z.number({ invalid_type_error: 'Enter a valid amount' }).refine((val) => val !== 0, 'Amount cannot be zero'),
  description: z.string().min(1, 'Description is required'),
})

/**
 * Form schema wrapping an array of transaction rows for batch creation
 */
const transactionsBatchSchema = z.object({
  transactions: z.array(transactionRowSchema).min(1, 'Add at least one transaction'),
})

type TransactionsBatchFormData = z.infer<typeof transactionsBatchSchema>

/** Default values for a new empty transaction row */
const DEFAULT_ROW = {
  facility_id: '',
  bank_account_id: '',
  category_id: '',
  transaction_date: dayjs().format('YYYY-MM-DD'),
  amount: 0,
  description: '',
}

/**
 * Category option shape used by both batch and edit forms.
 * `type` is nullable per DCP-250 Phase 3 — categories.type column is now
 * optional and no longer drives sign logic or grouping.
 */
interface CategoryOption {
  id: string
  name: string
  type: 'inflow' | 'outflow' | null
}

/**
 * Flat list of category select items.
 * Previously grouped into Inflow/Outflow sections; grouping is removed in
 * DCP-250 Phase 3 because `type` is now nullable and no longer meaningful
 * for display. Categories with null type must remain selectable.
 * Shared between batch and edit transaction forms.
 */
function CategorySelectOptions({ categories }: { categories: CategoryOption[] }) {
  return (
    <>
      {categories.map((c) => (
        <SelectItem key={c.id} value={c.id}>
          {c.name}
        </SelectItem>
      ))}
    </>
  )
}

/**
 * Props for ManualTransactionBatchFormDialog
 * @property open - Controls dialog visibility
 * @property onOpenChange - Called when dialog should open/close
 */
interface ManualTransactionBatchFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Modal dialog for creating multiple manual transactions at once.
 * Each row is rendered as a horizontal grid; users can add, copy, and remove rows.
 * Follows the same UX pattern as ProjectionBatchFormDialog.
 */
export function ManualTransactionBatchFormDialog({
  open,
  onOpenChange,
}: ManualTransactionBatchFormDialogProps) {
  const createTransactions = useCreateManualTransactions()
  const { user } = useAuth()
  const { data: facilitiesData, isLoading: facilitiesLoading } = useFacilities()
  const facilities = facilitiesData ?? []
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const categories = categoriesData ?? []
  const { data: bankAccountsData, isLoading: bankAccountsLoading } = useBankAccounts()
  const bankAccounts = useMemo(() => bankAccountsData ?? [], [bankAccountsData])

  const isDataLoading = facilitiesLoading || categoriesLoading || bankAccountsLoading

  /** Per-row selected bank institution name (UI-only filter, not persisted), keyed by field.id */
  const [selectedBanks, setSelectedBanks] = useState<Record<string, string>>({})

  /**
   * Precomputed lookup maps over `bankAccounts`, all built in a single
   * pass so the JSX hot path stays O(1) per row:
   *
   *   - `accountsByFacility`: `facility_id → BankAccount[]` — the bucket
   *     the Account dropdown reads through `getFilteredAccounts`.
   *   - `banksByFacility`: `facility_id → unique institution_name[]` —
   *     the option set for the Bank dropdown.
   *   - `accountsById`: `bank_account.id → BankAccount` — the lookup
   *     the `handleCopy` backfill effect uses to resolve a row's
   *     `bank_account_id` back to its `institution_name`. Previously
   *     that path called `bankAccounts.find()` per row per effect run
   *     (O(rows × accounts)); the Map flattens it to O(rows).
   *
   * All three maps invalidate together when `bankAccounts` changes,
   * which mirrors how the JSX consumers depend on the source.
   */
  const { accountsByFacility, banksByFacility, accountsById } = useMemo(() => {
    const accounts = new Map<string, typeof bankAccounts>()
    const banks = new Map<string, string[]>()
    const byId = new Map<string, typeof bankAccounts[number]>()
    for (const ba of bankAccounts) {
      byId.set(ba.id, ba)
      if (!ba.facility_id) continue
      const bucket = accounts.get(ba.facility_id)
      if (bucket) {
        bucket.push(ba)
      } else {
        accounts.set(ba.facility_id, [ba])
      }
    }
    for (const [facilityId, list] of accounts) {
      const seen = new Set<string>()
      const names: string[] = []
      for (const ba of list) {
        const name = ba.institution_name
        if (name && !seen.has(name)) {
          seen.add(name)
          names.push(name)
        }
      }
      banks.set(facilityId, names)
    }
    return { accountsByFacility: accounts, banksByFacility: banks, accountsById: byId }
  }, [bankAccounts])

  /**
   * `facility_id → BankAccount[]` for the given facility. Returns an empty
   * array when the facility is unset or not in the map — the user must
   * choose a facility before banks and accounts become selectable
   * (DCP-125: prevents a facility-mismatched transaction where the
   * selected account belongs to a different facility than the row's
   * `facility_id`).
   */
  const getAccountsForFacility = (facilityId: string | undefined) => {
    if (!facilityId) return []
    return accountsByFacility.get(facilityId) ?? []
  }

  /**
   * Unique bank institution names for the given facility. Reads directly
   * from the precomputed `banksByFacility` map so the Bank dropdown
   * options are O(1) per row.
   */
  const getBanksForFacility = (facilityId: string | undefined) => {
    if (!facilityId) return []
    return banksByFacility.get(facilityId) ?? []
  }

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<TransactionsBatchFormData>({
    resolver: zodResolver(transactionsBatchSchema),
    defaultValues: {
      transactions: [{ ...DEFAULT_ROW }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transactions',
  })

  /**
   * Backfill `selectedBanks` (UI-side state controlling the Bank dropdown
   * and the Account gate) from form data whenever the field array grows.
   *
   * Trigger paths:
   *  - `handleCopy(index)` duplicates a row with `bank_account_id` set, but
   *    the new field's RHF-generated `id` has no `selectedBanks` entry yet —
   *    so without this backfill, the new row's Bank dropdown showed "None"
   *    and the Account select stayed disabled even though the underlying
   *    `bank_account_id` was already set on the form (confusing UX: data
   *    is there, the UI says it isn't).
   *  - Any future caller that pre-populates rows (e.g. an import-from-clipboard
   *    feature) gets the same correct behaviour for free.
   *
   * Uses `getValues` (not `watch`) so a normal in-flight form edit doesn't
   * re-run this effect — it only fires when the row count or `bankAccounts`
   * source changes. The `setSelectedBanks` updater compares + early-returns
   * on no-op so React doesn't see a state churn on every `fields` identity
   * change either.
   *
   * Ordering / batching note: rapid back-to-back `handleCopy` calls schedule
   * multiple `append` updates which React batches into a single commit (or
   * commits sequentially under concurrent mode). Either way, the effect
   * fires *after* commit and reads `fields` + `getValues('transactions')`
   * fresh, so it always sees the current array — there's no intermediate
   * snapshot to miss. The functional `setSelectedBanks((prev) => ...)`
   * form is also order-safe across overlapping state updates: each call
   * reads the previous state and merges in any newly-discovered rows
   * without clobbering earlier merges.
   */
  useEffect(() => {
    if (accountsById.size === 0) return
    setSelectedBanks((prev) => {
      const current = getValues('transactions') ?? []
      let changed = false
      const next = { ...prev }
      fields.forEach((field, index) => {
        const baId = current[index]?.bank_account_id
        if (baId && !prev[field.id]) {
          // O(1) lookup via the precomputed `id → BankAccount` map instead
          // of bankAccounts.find() — at 30 rows × 100 accounts this is
          // 30 hash lookups instead of ~3000 equality checks.
          const account = accountsById.get(baId)
          if (account?.institution_name) {
            next[field.id] = account.institution_name
            changed = true
          }
        }
      })
      return changed ? next : prev
    })
  }, [fields, accountsById, getValues])

  /** Append a blank row to the form */
  const handleAddNew = () => {
    append({ ...DEFAULT_ROW })
  }

  /**
   * Duplicate the row at the given index. The new row's `selectedBanks`
   * UI-side state is reconciled by the `fields`-watching `useEffect` above
   * — it derives the bank institution from the freshly-copied
   * `bank_account_id` so the Bank dropdown and Account gate behave
   * identically to a manually-built row.
   *
   * @param index - Row index to copy
   */
  const handleCopy = (index: number) => {
    const row = watch(`transactions.${index}`)
    append({ ...row })
  }

  /**
   * Remove the row at the given index (minimum 1 row must remain)
   * @param index - Row index to remove
   */
  const handleRemove = (index: number) => {
    if (fields.length > 1) {
      const fieldId = fields[index].id
      remove(index)
      setSelectedBanks((prev) => {
        const next = { ...prev }
        delete next[fieldId]
        return next
      })
    } else {
      toast.error('At least one transaction is required')
    }
  }

  /**
   * Submit all transaction rows as a batch.
   * Maps each row to a payload including the current user ID, then
   * invokes the batch creation hook.
   * @param data - Validated form data
   */
  const onSubmit = async (data: TransactionsBatchFormData) => {
    try {
      // DCP-125 boundary check: the UI gates already prevent a user
      // from picking an account whose facility ≠ row.facility_id, but
      // form submit is the last hop before the data leaves the client.
      // Re-validate here against:
      //   - a future refactor that loosens the dropdown filter
      //   - DevTools edits to RHF state
      //   - any caller that bypasses the dialog and invokes the hook directly
      //
      // DCP-324 retired the ORIGINAL reason for this check — the two columns it
      // kept consistent are now one, since only `bank_account_id` is stored and
      // the facility is derived from it. What survives is the user-intent
      // check: Facility is the control that narrows the account list, so a row
      // whose account belongs elsewhere means the two selects have drifted and
      // the transaction would land on a facility the user never picked.
      for (let i = 0; i < data.transactions.length; i++) {
        const row = data.transactions[i]
        if (!row.bank_account_id) continue
        const ba = accountsById.get(row.bank_account_id)
        if (ba && ba.facility_id && row.facility_id && ba.facility_id !== row.facility_id) {
          toast.error(
            `Row ${i + 1}: selected account belongs to a different facility — please re-select.`,
          )
          return
        }
      }

      const payloads = data.transactions.map((row) => ({
        // DCP-324: `row.facility_id` is a UI-only field — it drives
        // `getAccountsForFacility` and the guard above, and is deliberately not
        // persisted. The account is what carries the facility.
        bank_account_id: row.bank_account_id || null,
        category_id: row.category_id || null,
        transaction_date: row.transaction_date,
        amount: row.amount,
        description: row.description,
        user_id: user?.id ?? null,
      }))

      await createTransactions.mutateAsync(payloads)
      const count = payloads.length
      toast.success(`${count} transaction${count > 1 ? 's' : ''} created successfully`)
      reset()
      setSelectedBanks({})
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create transactions'
      toast.error(message)
    }
  }

  /** Discard all changes and close the dialog */
  const handleDiscard = () => {
    reset()
    setSelectedBanks({})
    onOpenChange(false)
  }

  /**
   * Returns bank accounts filtered by BOTH the row's selected facility AND
   * the row's selected bank institution. The facility narrowing happens
   * first (see getAccountsForFacility) so the bank set already excludes
   * institutions with no accounts for that facility — DCP-125 specifically
   * required this: previously a Paradigm-only bank account was reachable
   * from a Nazareth-facility row.
   *
   * @param fieldId - Field ID from useFieldArray to get filtered accounts for
   * @param facilityId - The row's currently-selected facility_id
   */
  const getFilteredAccounts = (fieldId: string, facilityId: string | undefined) => {
    const bank = selectedBanks[fieldId]
    if (!bank) return []
    return getAccountsForFacility(facilityId).filter((ba) => ba.institution_name === bank)
  }

  return (
    <Modal
      isOpen={open}
      onClose={handleDiscard}
      title="Add Transactions"
      size="xl"
      className="!max-w-[1400px]"
      // DCP-332 (PResto 54821 #1): actions go in the Modal's `footer`, which renders
      // OUTSIDE the scroll container — the same pattern the single-transaction dialog
      // below already uses. With the form as the only scroller, buttons left inside it
      // sit past the end of a long row list. `footer` also draws the top border this
      // block used to add by hand (pt-4 border-t), and the submit button reaches the
      // form through `form="batch-transaction-form"`.
      //
      // Kept CONDITIONAL on `isDataLoading` so this stays a pure relocation: while the
      // accounts/categories load, the body renders a skeleton and no actions, exactly
      // as before — a footer rendered unconditionally would newly show Discard/Add
      // during loading.
      footer={
        isDataLoading ? undefined : (
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleDiscard}>
              Discard
            </Button>
            <Button
              type="submit"
              form="batch-transaction-form"
              disabled={createTransactions.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {createTransactions.isPending ? 'Adding...' : 'Add transactions'}
            </Button>
          </div>
        )
      }
    >
      <form id="batch-transaction-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {isDataLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* DCP-250: amounts are signed — one note for the whole batch so each
                row doesn't need its own hint. Positive = inflow, negative = outflow. */}
            <p className="text-sm text-muted-foreground px-2">
              Amounts are signed: enter a positive value for inflow, a negative value for outflow (e.g. -500).
            </p>
            {/* No own max-height / overflow on this rows list (DCP-332): same
                as the edit form below — the Modal body already scrolls, so this
                added a second, inner scrollbar once the batch had enough rows.
                Its max-h matched the modal's own 60vh, so it never bought any
                extra room either. */}
            <div className="space-y-4 px-2">
              {fields.map((field, index) => {
                // Read the row's `facility_id` once per render iteration and
                // reuse it everywhere the row gates on facility. Without
                // this, `watch(`transactions.${index}.facility_id`)` was
                // called seven times per row (Facility value, Bank disabled
                // / placeholder / option-source, Account disabled / placeholder /
                // option-source). RHF's `watch` is cheap but the duplication
                // muddied the reader's view of what the row's state actually
                // is — a single binding makes the dependency surface obvious.
                const rowFacilityId = watch(`transactions.${index}.facility_id`)
                const rowTransactionDate = watch(`transactions.${index}.transaction_date`)
                const rowBankAccountId = watch(`transactions.${index}.bank_account_id`)
                const rowCategoryId = watch(`transactions.${index}.category_id`)
                const rowSelectedBank = selectedBanks[field.id]
                return (
                <div key={field.id} className="px-4 pt-4 pb-10 border rounded-lg space-y-0">
                  <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_0.7fr_1fr_auto] gap-3 items-end">
                    {/* Facility */}
                    <div className="space-y-1.5">
                      {index === 0 && (
                        <Label className="text-xs font-medium bg-purple-100 text-purple-900 px-2 py-0.5 rounded inline-block">
                          Facility
                        </Label>
                      )}
                      {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                      <div className="relative">
                        <Select
                          value={rowFacilityId || 'none'}
                          onValueChange={(v) => {
                            const next = v === 'none' ? '' : v
                            setValue(`transactions.${index}.facility_id`, next)
                            // DCP-125: facility change invalidates the row's
                            // bank + account selections (they belong to the
                            // previous facility). Clear both so the user has
                            // to reselect from the facility-scoped list.
                            setSelectedBanks((prev) => ({ ...prev, [field.id]: '' }))
                            setValue(`transactions.${index}.bank_account_id`, '')
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select facility" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {facilities.map((f) => (
                              <SelectItem key={f.id} value={f.id}>
                                {f.name} {f.code && `(${f.code})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.transactions?.[index]?.facility_id && (
                          <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                            {errors.transactions[index]?.facility_id?.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Transaction Date */}
                    <div className="space-y-1.5">
                      {index === 0 && (
                        <Label className="text-xs font-medium">Transaction Date</Label>
                      )}
                      {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                      <div className="relative">
                        <DatePicker
                          date={
                            rowTransactionDate
                              ? (() => {
                                  const [y, m, d] = rowTransactionDate.split('-')
                                  return new Date(+y, +m - 1, +d)
                                })()
                              : undefined
                          }
                          onDateChange={(date) => {
                            if (!date) {
                              setValue(`transactions.${index}.transaction_date`, '')
                              return
                            }
                            setValue(
                              `transactions.${index}.transaction_date`,
                              dayjs(date).format('YYYY-MM-DD'),
                            )
                          }}
                          placeholder="Select date"
                          className="w-full"
                        />
                        {errors.transactions?.[index]?.transaction_date && (
                          <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                            {errors.transactions[index]?.transaction_date?.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Bank (UI-only filter — not persisted) */}
                    <div className="space-y-1.5">
                      {index === 0 && (
                        <Label className="text-xs font-medium flex items-center gap-1">
                          Bank
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Manage banks in Settings &rarr; Banking
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                      )}
                      <Select
                        value={rowSelectedBank || 'none'}
                        onValueChange={(v) => {
                          const bank = v === 'none' ? '' : v
                          setSelectedBanks((prev) => ({ ...prev, [field.id]: bank }))
                          // Clear the account when bank changes
                          setValue(`transactions.${index}.bank_account_id`, '')
                        }}
                        disabled={!rowFacilityId}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={rowFacilityId ? 'Select bank' : 'Select facility first'}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {getBanksForFacility(rowFacilityId).map((name) => (
                            <SelectItem key={name} value={name}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Account (filtered by selected facility + bank) */}
                    <div className="space-y-1.5">
                      {index === 0 && (
                        <Label className="text-xs font-medium flex items-center gap-1">
                          Account
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Manage accounts in Settings &rarr; Banking
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                      )}
                      {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                      <div className="relative">
                        <Select
                          value={rowBankAccountId || 'none'}
                          onValueChange={(v) =>
                            setValue(`transactions.${index}.bank_account_id`, v === 'none' ? '' : v)
                          }
                          // Defense-in-depth: even though the Bank select is
                          // already disabled without a facility (and changing
                          // facility clears the row's selectedBanks entry),
                          // make the facility-first invariant explicit on
                          // Account too so future refactors can't accidentally
                          // expose a facility-less account picker. Mirrors the
                          // same `(no facility) || (no bank)` gate on the edit
                          // dialog's Account select (`ManualTransactionFormDialog`
                          // body further down — search `disabled={!watchedFacilityId`
                          // to land on it). Descriptive rather than line-number
                          // reference so the two stay in sync after any future
                          // reshuffle.
                          disabled={!rowFacilityId || !rowSelectedBank}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                !rowFacilityId
                                  ? 'Select facility first'
                                  : rowSelectedBank
                                  ? 'Select account'
                                  : 'Select bank first'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {getFilteredAccounts(field.id, rowFacilityId).map((ba) => (
                              <SelectItem key={ba.id} value={ba.id}>
                                {ba.name}
                                {ba.account_number ? ` (${maskAccountNumber(ba.account_number)})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.transactions?.[index]?.bank_account_id && (
                          <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                            {errors.transactions[index]?.bank_account_id?.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Transaction Category */}
                    <div className="space-y-1.5">
                      {index === 0 && (
                        <Label className="text-xs font-medium">Transaction category</Label>
                      )}
                      {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                      <div className="relative">
                        <Select
                          value={rowCategoryId}
                          onValueChange={(v) => setValue(`transactions.${index}.category_id`, v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <CategorySelectOptions categories={categories} />
                          </SelectContent>
                        </Select>
                        {errors.transactions?.[index]?.category_id && (
                          <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                            {errors.transactions[index]?.category_id?.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Amount — DCP-224/225: locale-stable AmountInput instead of type="number" */}
                    <div className="space-y-1.5">
                      {index === 0 && (
                        <Label className="text-xs font-medium">Amount</Label>
                      )}
                      {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                      <div className="relative">
                        <Controller
                          name={`transactions.${index}.amount`}
                          control={control}
                          render={({ field }) => (
                            <AmountInput
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="0.00"
                            />
                          )}
                        />
                        {errors.transactions?.[index]?.amount && (
                          <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                            {errors.transactions[index]?.amount?.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                      {index === 0 && (
                        <Label className="text-xs font-medium">Description</Label>
                      )}
                      {/* relative wrapper keeps the error out of flow so sibling cells stay aligned */}
                      <div className="relative">
                        <Input
                          placeholder="Type here"
                          {...register(`transactions.${index}.description`)}
                        />
                        {errors.transactions?.[index]?.description && (
                          <p className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                            {errors.transactions[index]?.description?.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons — bottom-aligned via items-end on the grid */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(index)}
                        className="h-9 w-9 p-0"
                        aria-label="Copy row"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(index)}
                        disabled={fields.length === 1}
                        className="h-9 w-9 p-0"
                        aria-label="Remove row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )
              })}

              {/* Add New Button */}
              <Button type="button" variant="outline" onClick={handleAddNew} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add new
              </Button>
            </div>
          </>
        )}
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Edit-only single transaction dialog
// ---------------------------------------------------------------------------

/**
 * Zod schema for the edit transaction form.
 *
 * Mirrors the batch schema, including the UI-only `facility_id`: required so
 * the Bank / Account selects have something to narrow by, seeded from the
 * account's facility on open, and not persisted (DCP-324).
 */
const manualTransactionSchema = z.object({
  facility_id: z.string().min(1, 'Facility is required'),
  bank_account_id: z.string().min(1, 'Account is required'),
  category_id: z.string().min(1, 'Category is required'),
  transaction_date: z.string().min(1, 'Date is required'),
  amount: z.number({ invalid_type_error: 'Enter a valid amount' }).refine((val) => val !== 0, 'Amount cannot be zero'),
  description: z.string().min(1, 'Description is required'),
})

type ManualTransactionFormValues = z.infer<typeof manualTransactionSchema>

/**
 * Props for the edit-only ManualTransactionFormDialog
 * @property open - Controls dialog visibility
 * @property onOpenChange - Called when dialog should open/close
 * @property transaction - The transaction to edit (required)
 */
interface ManualTransactionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transaction?: TenantTransaction
}

/**
 * Dialog for editing a single manual transaction.
 * Uses a vertical form layout with all transaction fields.
 * This component is edit-only; for creating transactions use ManualTransactionBatchFormDialog.
 */
export function ManualTransactionFormDialog({
  open,
  onOpenChange,
  transaction,
}: ManualTransactionFormDialogProps) {
  const updateTransaction = useUpdateManualTransaction()
  // Full list (incl. inactive): this dialog SEEDS `facility_id` from an existing
  // record (PR #183 review). With an active-only list a record pointing at a
  // since-deactivated facility renders the Select's placeholder — Radix shows it
  // when no SelectItem matches — while react-hook-form still holds the real id.
  // A user "fixing" the apparently-empty required field then silently reassigns
  // the record. The lists that reach this dialog are scoped elsewhere, so this
  // only covers the cross-tab window.
  const { data: facilitiesData } = useFacilities({ activeOnly: false })
  const facilities = facilitiesData ?? []
  const { data: categoriesData } = useCategories()
  const categories = categoriesData ?? []
  const { data: bankAccountsData, isLoading: bankAccountsLoading } = useBankAccounts()
  const activeBankAccounts = useMemo(() => bankAccountsData ?? [], [bankAccountsData])

  /**
   * If the transaction references a bank account that's been soft-deleted
   * (DCP-222), the active list won't include it — pull it in via the
   * include-deleted helper so the dropdown can still display the
   * original Bank / Account Name / Account Number, marked as
   * "(deleted)" and locked from re-selection. Single-id case for the
   * edit dialog; only fires when the referenced id is missing from
   * the active list.
   *
   * Two guards before the lookup fires:
   *   1. `bankAccountsLoading` — while the active list is still loading,
   *      ANY non-null `bank_account_id` looks "missing" from the
   *      fallback-empty array and would trigger a wasted lookup for an
   *      account that's almost always active.
   *   2. `!bankAccountsData` — if `useBankAccounts` finished with an
   *      error, `isLoading` flips to false but `data` stays undefined.
   *      Falling through would not only waste a request but
   *      mis-classify an actually-active account as soft-deleted in
   *      the rendered dropdown (because `activeIdSet` further down is
   *      empty). Falsy data means we skip the lookup entirely until
   *      the parent query recovers.
   */
  const referencedDeletedIds = useMemo(() => {
    if (!transaction?.bank_account_id || bankAccountsLoading || !bankAccountsData) return []
    const isInActive = activeBankAccounts.some((a) => a.id === transaction.bank_account_id)
    return isInActive ? [] : [transaction.bank_account_id]
  }, [transaction?.bank_account_id, activeBankAccounts, bankAccountsLoading, bankAccountsData])
  const { data: referencedDeletedData } = useBankAccountsByIds(referencedDeletedIds)
  const referencedDeleted = useMemo(() => referencedDeletedData ?? [], [referencedDeletedData])

  /**
   * Combined list: active accounts + any soft-deleted account currently
   * referenced by the open transaction. Items carry an `isDeleted` flag
   * so the rendering layer can mark them disabled with a "(deleted)"
   * suffix. Active items get `isDeleted: false` so the type is uniform.
   *
   * The `referencedDeleted` half is defensively filtered against
   * `activeBankAccounts` by `id` — if a row appears in both lists
   * (e.g. mid-flight cache during a hypothetical restore flow, or a
   * race between the two React Query keys), we keep the active variant
   * and drop the deleted one. Without this filter the resulting array
   * would carry the same `id` twice with conflicting `isDeleted` flags,
   * and the Bank/Account dropdowns would render duplicate options.
   */
  const activeIdSet = useMemo(
    () => new Set(activeBankAccounts.map((a) => a.id)),
    [activeBankAccounts],
  )
  const bankAccounts = useMemo(() => {
    // Both halves use a uniform `{ ...a, isDeleted }` spread so any
    // future column added to `bank_accounts` (via `pnpm db:types`
    // regeneration) flows into both shapes automatically. Earlier
    // revisions of this code hand-listed nine fields in the deleted
    // half, which would have silently desync'd from the active half
    // the moment a new column landed. `deleted_at` is intentionally
    // kept on the surfaced row — it's already part of `BankAccountRow`
    // after the DCP-222 migration (nullable on active, timestamp on
    // deleted), so leaving it in costs nothing and keeps the two
    // halves structurally aligned.
    const withFlag = activeBankAccounts.map((a) => ({ ...a, isDeleted: false as const }))
    const deleted = referencedDeleted
      .filter((a) => !activeIdSet.has(a.id))
      .map((a) => ({ ...a, isDeleted: true as const }))
    return [...withFlag, ...deleted]
  }, [activeBankAccounts, referencedDeleted, activeIdSet])

  /** Selected bank institution name (UI-only filter, not persisted) */
  const [selectedBank, setSelectedBank] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<ManualTransactionFormValues>({
    resolver: zodResolver(manualTransactionSchema),
  })

  /** Watched form values used to narrow bank / account choices by facility. */
  const watchedFacilityId = watch('facility_id')

  /**
   * Bank accounts visible for the currently-selected facility. An unset
   * facility means no accounts are reachable — the user must pick a
   * facility first (DCP-125: prevents editing the transaction to point
   * at a bank account that belongs to a different facility, which the UI
   * previously allowed).
   */
  const accountsForFacility = useMemo(
    () => (watchedFacilityId ? bankAccounts.filter((ba) => ba.facility_id === watchedFacilityId) : []),
    [bankAccounts, watchedFacilityId],
  )

  /**
   * Unique bank institution names available for the selected facility.
   * Derived from `accountsForFacility` so banks with no accounts under
   * the chosen facility don't appear in the dropdown at all.
   */
  const uniqueBanks = useMemo(
    () => [...new Set(accountsForFacility.map((ba) => ba.institution_name).filter(Boolean))] as string[],
    [accountsForFacility],
  )

  /**
   * Pre-grouped lookup: `institution_name → accounts[]`, scoped to the
   * **currently-selected facility** (NOT the global `bankAccounts` list).
   * Built once per `accountsForFacility` change and reused inside the
   * Bank dropdown render loop to compute the `allDeleted` flag in O(1)
   * per option instead of O(n) per option.
   *
   * Facility scoping is critical: a bank label is shown as "(deleted)"
   * only when every account *visible to this facility* is soft-deleted.
   * If we grouped over the global list, an account with the same
   * institution_name in another facility (active) would mask the
   * deletion in the current facility and the user would see the bank
   * as selectable even though no live account is reachable here.
   * Empty/null `institution_name` rows are excluded — those never
   * appear in `uniqueBanks` either.
   */
  type FacilityScopedBankAccount = typeof accountsForFacility[number]
  const accountsByBankName = useMemo(() => {
    const map = new Map<string, FacilityScopedBankAccount[]>()
    for (const a of accountsForFacility) {
      if (!a.institution_name) continue
      const bucket = map.get(a.institution_name)
      if (bucket) bucket.push(a)
      else map.set(a.institution_name, [a])
    }
    return map
  }, [accountsForFacility])

  /**
   * Bank accounts narrowed further by the selected bank institution.
   * Falls back to the transaction's stored bank only when both the
   * facility AND the bank-name state still match it (first render
   * before useEffect fires). The fallback intentionally enforces the
   * facility match too — a transaction whose stored facility/bank no
   * longer agree (legacy data from before this fix) should not surface
   * mismatched accounts.
   */
  // Narrow the dep to the only field actually read from `transaction`, so
  // unrelated edits (amount, description) don't churn the memo and
  // re-render every account row downstream.
  const transactionBankName = transaction?.bank_accounts?.institution_name
  const filteredAccounts = useMemo(() => {
    const bank = selectedBank || transactionBankName
    if (!bank) return []
    return accountsForFacility.filter((ba) => ba.institution_name === bank)
  }, [accountsForFacility, selectedBank, transactionBankName])

  /**
   * Reset form whenever the dialog opens or the target transaction changes.
   * Populates fields with the transaction data for editing.
   * Derives the initial bank filter from the transaction's bank_accounts relation.
   *
   * DCP-324: the Facility field is seeded from the row's ACCOUNT
   * (`bank_accounts.facility_id`), which is the only place a transaction's
   * facility exists now. That also RETIRES the legacy-mismatch guard that used
   * to sit here: it compared the transaction's stored `facility_id` against its
   * account's and logged a warning when a pre-DCP-125 row disagreed. With one
   * value derived from the other the comparison is `x !== x` — the mismatch
   * class it detected cannot be represented any more, and every row that
   * carried it is now displayed and edited under its account's current
   * facility instead of a stale copy.
   */
  useEffect(() => {
    if (open && transaction) {
      reset({
        facility_id: transaction.bank_accounts?.facility_id ?? '',
        bank_account_id: transaction.bank_account_id ?? '',
        category_id: transaction.category_id ?? '',
        transaction_date: transaction.transaction_date,
        amount: transaction.amount,
        description: transaction.description,
      })
      // Derive initial bank from the transaction's bank_accounts relation
      const bankName = transaction.bank_accounts?.institution_name ?? ''
      setSelectedBank(bankName)
    }
  }, [transaction, open, reset])

  /** Close dialog and reset form state */
  const handleClose = () => {
    reset()
    setSelectedBank('')
    onOpenChange(false)
  }

  /**
   * Handle form submission — updates the existing transaction.
   * @param values - Validated form values from react-hook-form
   */
  const onSubmit = async (values: ManualTransactionFormValues) => {
    if (!transaction) return

    try {
      // DCP-125 boundary check (mirror of the batch form): the dropdown
      // gates prevent picking a foreign-facility account, but form submit
      // is the last hop before mutation. Re-validate to catch a future
      // refactor that loosens the gate, or DevTools edits to RHF state.
      //
      // DCP-324 changed what this protects, not whether it is needed. It is no
      // longer about keeping two stored columns consistent — only
      // `bank_account_id` is written now, and the facility follows it. It is
      // about the USER's intent: the Facility select is how they narrowed the
      // account list, so submitting an account from a different facility means
      // the two controls have drifted out of step and the row would silently
      // land somewhere they did not choose.
      if (values.bank_account_id) {
        const ba = bankAccounts.find((a) => a.id === values.bank_account_id)
        if (ba && ba.facility_id && values.facility_id && ba.facility_id !== values.facility_id) {
          toast.error('Selected account belongs to a different facility — please re-select.')
          return
        }
      }

      await updateTransaction.mutateAsync({
        id: transaction.id,
        data: {
          // DCP-324: `facility_id` is NOT sent. The account carries it; see
          // `ManualTransactionInput`.
          bank_account_id: values.bank_account_id || null,
          category_id: values.category_id || null,
          transaction_date: values.transaction_date,
          amount: values.amount,
          description: values.description,
        },
      })
      toast.success('Transaction updated')
      handleClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update transaction'
      toast.error(message)
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Edit Transaction"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-transaction-form"
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={updateTransaction.isPending}
          >
            {updateTransaction.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Update
          </Button>
        </div>
      }
    >
      {/* ---------------------------------------------------------------------
          DIALOG INVARIANT (DCP-337): every Select below must ignore a value
          that is not one of its own currently rendered options.

          Radix keeps a hidden native <select> in sync with the controlled
          `value`, but registers its <option> list from a child layout effect —
          so the two can disagree for a commit. Whenever `value` moves to
          something the currently registered option set does not contain, the
          native select cannot take it, resets to '', and Radix dispatches that
          '' back through onValueChange indistinguishably from a real user pick.

          Two ways to hit it. Only the first is specific to this dialog; the
          second belongs to the MOUNT PATTERN and applies to any dialog a page
          keeps permanently mounted while flipping only `open` (no `key`):
            1. value and options land in the SAME commit — the reported bug,
               and this one is local: the seeding effect sets `selectedBank` in
               the same commit that first fills `uniqueBanks` (both derive from
               `facility_id`, which it also seeds), so Bank + Account blanked on
               the first open.
            2. options land LATER than the value — the dialog is permanently
               mounted, so `useFacilities`/`useCategories` race the user's
               click. Opening before they resolve seeds `facility_id` /
               `category_id` against an option list that is still empty.
               `useCategories` is requested by nothing else on
               TransactionsImportPage, so nothing warms its cache.
               ProjectionFormDialog is mounted the same way and had the same
               live defect — guarded in the same commit as this comment. Any new
               permanently-mounted dialog needs the same treatment, unless it
               follows the immune pattern instead: ManualTransactionBatchFormDialog
               (below in this file) refuses to render its form while
               `isDataLoading`, and EditManualAccountDialog renders a spinner in
               place of its Select — both guarantee options exist before a value
               can land.

          A real selection always carries one of the rendered option values, so
          dropping everything else costs nothing and is not order-dependent.

          This is an unfixed upstream bug: radix-ui/primitives#2817 (root),
          #3693 (filed against our 2.2.6), fix PR #3759 still unmerged. When
          that lands and we upgrade, every guard below can be deleted.
          --------------------------------------------------------------------- */}
      {/* No own max-height / overflow on this form (DCP-332): the saas-ui Modal
          body is already a scroll container (max-h-[60vh] overflow-y-auto), so a
          nested one produced a second, inner scrollbar — and the inner
          max-h-[70vh] could never be reached anyway, since the outer wrapper
          already caps the available height at 60vh. Letting the form flow makes
          the modal body the single scroller (same fix as DCP-313 / DCP-312 /
          DCP-336, and identical to the one applied to ProjectionFormDialog). */}
      <form id="edit-transaction-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-1">
        {/* Facility */}
        <div className="space-y-2">
          <Label htmlFor="facility_id">
            Facility <span className="text-destructive">*</span>
          </Label>
          <Select
            value={watch('facility_id') || 'none'}
            onValueChange={(v) => {
              // Dialog invariant (see above). Worst case of the four: this
              // handler also runs the DCP-125 cascade below, so one echo blanks
              // Facility, Bank AND Account — in form state, not just visually.
              if (v !== 'none' && !facilities.some((f) => f.id === v)) return
              const next = v === 'none' ? '' : v
              setValue('facility_id', next)
              // DCP-125: facility change invalidates bank + account
              // selections (they belong to the previous facility).
              // Clear both so the user has to pick from the
              // facility-scoped list.
              setSelectedBank('')
              setValue('bank_account_id', '')
            }}
          >
            <SelectTrigger id="facility_id">
              <SelectValue placeholder="Select facility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {facilities.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name} {f.code && `(${f.code})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.facility_id && (
            <p className="text-sm text-destructive">{errors.facility_id.message}</p>
          )}
        </div>

        {/* Transaction Date */}
        <div className="space-y-2">
          <Label htmlFor="transaction_date">
            Transaction Date <span className="text-destructive">*</span>
          </Label>
          <DatePicker
            date={
              watch('transaction_date')
                ? (() => {
                    const [y, m, d] = watch('transaction_date').split('-')
                    return new Date(+y, +m - 1, +d)
                  })()
                : undefined
            }
            onDateChange={(date) => {
              if (!date) {
                setValue('transaction_date', '')
                return
              }
              setValue('transaction_date', dayjs(date).format('YYYY-MM-DD'))
            }}
            placeholder="Select date"
            className="w-full"
          />
          {errors.transaction_date && (
            <p className="text-sm text-destructive">{errors.transaction_date.message}</p>
          )}
        </div>

        {/* Bank (UI-only filter — not persisted) */}
        <div className="space-y-2">
          <Label htmlFor="bank" className="flex items-center gap-1">
            Bank <span className="text-destructive">*</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="top">
                Manage banks in Settings &rarr; Banking
              </TooltipContent>
            </Tooltip>
          </Label>
          <Select
            value={selectedBank || 'none'}
            onValueChange={(v) => {
              // Dialog invariant (see the block above the form). This is the
              // case-1 handler from the original bug report: without the guard
              // the echo cleared `selectedBank` and, via the line below,
              // `bank_account_id`.
              if (v !== 'none' && !uniqueBanks.includes(v)) return
              const bank = v === 'none' ? '' : v
              setSelectedBank(bank)
              // Clear the account when bank changes
              setValue('bank_account_id', '')
            }}
            disabled={!watchedFacilityId}
          >
            <SelectTrigger id="bank">
              <SelectValue
                placeholder={watchedFacilityId ? 'Select bank' : 'Select facility first'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {uniqueBanks.map((name) => {
                // A bank label is considered deleted only when EVERY
                // account with that institution_name in our combined
                // list is soft-deleted. If even one active account
                // shares the name, the user can still pick the live
                // option, so we render the label without the marker
                // (DCP-222). O(1) lookup via the pre-grouped map.
                const accountsWithName = accountsByBankName.get(name) ?? []
                const allDeleted =
                  accountsWithName.length > 0 &&
                  accountsWithName.every((a) => a.isDeleted)
                return (
                  <SelectItem key={name} value={name} disabled={allDeleted}>
                    {name}
                    {allDeleted ? ' (deleted)' : ''}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Account (filtered by selected facility + bank) */}
        <div className="space-y-2">
          <Label htmlFor="bank_account_id" className="flex items-center gap-1">
            Account <span className="text-destructive">*</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="top">
                Manage accounts in Settings &rarr; Banking
              </TooltipContent>
            </Tooltip>
          </Label>
          <Select
            value={watch('bank_account_id') || 'none'}
            onValueChange={(v) => {
              // Dialog invariant (see the block above the form). Needed
              // independently of the Bank guard: `filteredAccounts` fills on the
              // same commit, so this Select produced its own '' echo that blanked
              // the Account even once Bank was guarded.
              if (v !== 'none' && !filteredAccounts.some((ba) => ba.id === v)) return
              setValue('bank_account_id', v === 'none' ? '' : v)
            }}
            // Account disabled until both facility AND bank are set. The
            // `selectedBank` checked here is the UI-side filter populated
            // by the bank dropdown's onValueChange; the
            // `transaction?.bank_accounts?.institution_name` fallback exists
            // because the `useEffect` earlier in this component body that
            // seeds `selectedBank` from the loaded transaction (search this
            // file for `setSelectedBank(bankName)`) runs *after* the first
            // paint —
            // without the fallback, the Account select would render
            // momentarily disabled on every Edit-dialog open for an
            // existing transaction that already has a bank, then flip
            // enabled, which is a visible flicker.
            disabled={!watchedFacilityId || (!selectedBank && !transaction?.bank_accounts?.institution_name)}
          >
            <SelectTrigger id="bank_account_id">
              <SelectValue
                placeholder={
                  !watchedFacilityId
                    ? 'Select facility first'
                    : selectedBank
                    ? 'Select account'
                    : 'Select bank first'
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {filteredAccounts.map((ba) => (
                // Soft-deleted accounts (DCP-222) are surfaced disabled
                // with a "(deleted)" suffix so the original Account
                // selection on the transaction remains visible in the
                // trigger but the user can't re-attach a new transaction
                // to a removed account.
                <SelectItem key={ba.id} value={ba.id} disabled={ba.isDeleted}>
                  {ba.name}
                  {ba.account_number ? ` (${maskAccountNumber(ba.account_number)})` : ''}
                  {ba.isDeleted ? ' (deleted)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.bank_account_id && (
            <p className="text-sm text-destructive">{errors.bank_account_id.message}</p>
          )}
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="category_id">
            Transaction Category <span className="text-destructive">*</span>
          </Label>
          <Select
            // `?? ''` keeps this Select controlled from the very first render:
            // with `undefined` Radix treats it as uncontrolled for that render
            // and React logs "changing from uncontrolled to controlled".
            value={watch('category_id') ?? ''}
            onValueChange={(v) => {
              // Dialog invariant (see the block above the form). No 'none'
              // sentinel here — CategorySelectOptions renders real ids only.
              if (!categories.some((c) => c.id === v)) return
              setValue('category_id', v)
            }}
          >
            <SelectTrigger id="category_id">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <CategorySelectOptions categories={categories} />
            </SelectContent>
          </Select>
          {errors.category_id && (
            <p className="text-sm text-destructive">{errors.category_id.message}</p>
          )}
        </div>

        {/* Amount — DCP-224/225: locale-stable AmountInput instead of type="number" */}
        <div className="space-y-2">
          <Label htmlFor="amount">
            Amount <span className="text-destructive">*</span>
          </Label>
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <AmountInput
                id="amount"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="e.g. 1000 (inflow) or -500 (outflow)"
              />
            )}
          />
          {/* DCP-250: sign comes from the value — tell the user what it means. */}
          <p className="text-sm text-muted-foreground">
            Positive = inflow, negative = outflow.
          </p>
          {errors.amount && (
            <p className="text-sm text-destructive">{errors.amount.message}</p>
          )}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">
            Description <span className="text-destructive">*</span>
          </Label>
          <Input id="description" placeholder="Brief description" {...register('description')} />
          {errors.description && (
            <p className="text-sm text-destructive">{errors.description.message}</p>
          )}
        </div>
      </form>
    </Modal>
  )
}
